import React from "react";

import type { IconProps } from "../icons";
import { IconChevronDown } from "../icons";
import { match } from "ts-pattern";
import { DivLink } from "../Link";
import { Menu } from "../Menu";
import { TestableElement } from "../TestableElement";

interface Linkable {
  linkTo?: string;
  linkTarget?: "_blank" | "_self" | "_parent" | "_top";
}

interface Clickable {
  onClick?: ((e: any) => Promise<boolean>) | ((e: any) => void);
}

interface MenuOptions {
  options?: React.ReactNode[];
  optionsAlign?: "center" | "start" | "end";
}

export interface BaseButtonProps extends MenuOptions, Linkable, Clickable, TestableElement {
  children: React.ReactNode;
  ariaLabel?: string;
  loading?: boolean;
  type?: "button" | "submit";
  size?: "xxs" | "xs" | "sm" | "base" | "lg";
  spanButton?: boolean;
  icon?: React.ComponentType<IconProps>;
  iconSize?: number;
  disabled?: boolean;
  className?: string;
}

interface UnstyledButtonProps extends BaseButtonProps {
  className?: string;
  spinner?: React.ReactNode;
}

export function UnstyledButton(props: UnstyledButtonProps) {
  if (props.linkTo && props.onClick) {
    throw new Error("Button cannot have both linkTo and onClick props");
  }

  if (props.linkTo && props.type === "submit") {
    throw new Error("Button cannot have both linkTo and type='submit' props");
  }

  if (props.linkTarget && !props.linkTo) {
    throw new Error("Button cannot have linkTarget without linkTo prop");
  }

  if (props.linkTo) {
    return UnstyledLinkButton(props);
  } else if (props.spanButton) {
    return UnstyledSpanButton(props);
  } else if (props.options) {
    return UnstyledMenuButton(props);
  } else {
    return UnstyledActionButton(props);
  }
}

function UnstyledLinkButton(props: UnstyledButtonProps) {
  const to = props.linkTo!;
  const external = to.startsWith("http://") || to.startsWith("https://");

  return (
    <DivLink className={props.className} to={to} target={props.linkTarget} testId={props.testId} external={external}>
      {props.children}
      {props.spinner}
    </DivLink>
  );
}

// if no text then it's an icon-only button and we need different padding rules
function hasVisibleTextLabel(children: React.ReactNode): boolean {
  const nodes = React.Children.toArray(children);
  if (nodes.length === 0) return false;

  for (const node of nodes) {
    if (typeof node === "string" && node.trim() !== "") return true;
    if (typeof node === "number") return true;
    if (React.isValidElement(node)) {
      const cls = (node.props as { className?: string } | undefined)?.className;
      const isScreenReaderOnly = typeof cls === "string" && cls.includes("sr-only");
      if (!isScreenReaderOnly) return true;
    }
  }

  return false;
}

function UnstyledActionButton(props: UnstyledButtonProps) {
  const disabled = props.disabled || props.loading;

  const handleClick = (e: any) => {
    if (disabled) return;
    if (props.onClick) props.onClick(e);
  };

  const type = props.type || "button";
  const iconSize =
    props.iconSize ||
    match(props.size || "base")
      .with("xxs", () => 12)
      .with("xs", () => 14)
      .with("sm", () => 16)
      .with("base", () => 18)
      .with("lg", () => 20)
      .exhaustive();

  let children = props.children;
  if (props.icon) {
    const hasLabel = hasVisibleTextLabel(props.children);
    children = hasLabel ? (
      <div className="-ml-1 flex items-center gap-1">
        <props.icon size={iconSize} />
        {props.children}
      </div>
    ) : (
      <props.icon size={iconSize} />
    );
  }

  return (
    <button
      type={type}
      className={props.className}
      onClick={handleClick}
      disabled={disabled}
      aria-label={props.ariaLabel}
      data-test-id={props.testId}
    >
      {children}
      {props.spinner}
    </button>
  );
}

function UnstyledMenuButton(props: UnstyledButtonProps) {
  if (props.options === undefined) {
    throw "Menu button must have options";
  }

  const triggerClass = props.className + " " + "inline-flex items-center gap-2";

  const trigger = (
    <div className={triggerClass} data-test-id={props.testId}>
      {props.children}
      <IconChevronDown size={16} />
    </div>
  );

  return <Menu customTrigger={trigger}>{props.options}</Menu>;
}

function UnstyledSpanButton(props: UnstyledButtonProps) {
  const handleClick = (e: any) => {
    if (props.loading) return;
    if (props.onClick) props.onClick(e);
  };

  return (
    <span className={props.className} onClick={handleClick} data-test-id={props.testId}>
      {props.children}
      {props.spinner}
    </span>
  );
}
